<template>
    <div class="list-modules apprentice-simple-card">
        <div
            class="module-items"
            v-if="allModules.length">
            <design-2021-apprentice-data-table
                class="module-management-table"
                :fields="fields"
                :currentPage="currentPage"
                :items="modules"
                ref="modulesListTable"
                @context-changed="contextChanged">
                <template v-slot:options>
                    <div class="tools">
                        <design-2021-apprentice-select-search-component
                            class="module-tag-select"
                            :selected-in="tagFilter"
                            label="name"
                            :options="availableTags"
                            :placeholder="'Filter op tag'"
                            :trackBy="'name'"
                            @input="update">
                        </design-2021-apprentice-select-search-component>

                        <b-button
                            class="btn-download btn-rating-download"
                            variant="primary"
                            :href="downloadUrl"
                            target="_blank"
                            >{{ $t('common.download') }}
                        </b-button>

                        <b-button
                            class="btn-download btn-rating-download"
                            variant="primary"
                            :href="exportUrl"
                            target="_blank"
                            >Export
                        </b-button>
                        <b-button
                            class="btn-add btn-add-module"
                            v-b-modal="'module-index-create-module'"
                            v-if="$authUser.hasRole('module') || $authUser.isAdmin()"
                            variant="primary"
                            @click="openCreateModal()">
                            {{ $t('modules.new') }}
                        </b-button>
                    </div>
                </template>

                <template v-slot:cell(tag)="data">
                    {{ data.item.module_tags[0].name }}
                </template>

                <template v-slot:cell(teachers)="data">
                    {{ getTeachers(data) }}
                </template>

                <template v-slot:cell(dates)="data">
                    <div v-for="date in data.item.module_dates">
                        {{ date.start_date | date_named_month_short }}
                    </div>
                </template>

                <template v-slot:cell(students)="data">
                    <div v-for="date in data.item.module_dates">
                        <span class="module-students">{{ date.module_users.length }}</span>
                        <b-badge
                            v-if="date.module_users.length >= date.max"
                            variant="danger"
                            >vol</b-badge
                        >
                        <b-badge
                            v-if="date.max - date.module_users.length < 5 && date.max - date.module_users.length > 0"
                            variant="warning">
                            {{ $t('modules.almost_full') }}
                        </b-badge>
                    </div>
                </template>

                <template v-slot:cell(actions)="data">
                    <div class="actions">
                        <b-button
                            class="btn-open btn-show-module-tree remove-icon-margin"
                            v-b-tooltip.hover
                            variant="primary-icon-only"
                            :title="$t('modules.almost_full')"
                            @click="redirectModule(data.item)">
                        </b-button>

                        <b-button
                            class="btn-download btn-rating-download"
                            variant="primary-icon-only"
                            @click="moduleDownload(data.item.id)"
                            target="_blank">
                        </b-button>
                        <b-button
                            class="btn-copy btn-copy-module-tree remove-icon-margin"
                            v-b-tooltip.hover
                            v-if="$authUser.hasRole('module') || $authUser.isAdmin()"
                            variant="primary-icon-only"
                            :title="$t('modules.copy_module')"
                            @click="openCreateModal(data.item)">
                        </b-button>
                        <b-button
                            class="btn-remove btn-remove-module-tree remove-icon-margin"
                            v-b-tooltip.hover
                            v-if="$authUser.hasRole('module') || $authUser.isAdmin()"
                            variant="danger-icon-only"
                            :title="$t('modules.delete_module')"
                            @click="removeModule(data.item)">
                        </b-button>
                    </div>
                </template>
            </design-2021-apprentice-data-table>
        </div>
        <div
            class="not-found with-header module-not-found"
            v-else>
            <div
                class="btn-add-circle icon"
                v-b-modal="'module-index-create-module'"></div>
            <div class="alert-notice">{{ $t('modules.no_modules_found') }}</div>
        </div>
        <design-2021-module-index-create-module-modal-component
            :ref="'create-module-modal'"
            :education-levels-in="educationLevelsIn"
            :skill-trees-in="skillTreesIn"
            :tags-in="tagsIn"
            :teachers-in="teachersIn"
            :las-groups-in="lasGroupsIn"
            :module-periods-in="modulePeriodsIn">
        </design-2021-module-index-create-module-modal-component>
    </div>
</template>

<script>
import { ords } from '../../app';
import moment from 'moment';

const moduleOrds = ords.use('module');
const moduleTagsOrds = ords.use('module_tags');

export default {
    name: 'design-2021-module-index-component',
    props: {
        modulesIn: Array,
        tagsIn: Array,
        educationLevelsIn: Array,
        skillTreesIn: Array,
        teachersIn: Array,
        lasGroupsIn: Array,
        modulePeriodsIn: Array,
    },
    data() {
        return {
            allModules: [],
            tags: [],
            tagFilter: {
                id: null,
                name: 'Alle tags',
            },
            fields: [
                {
                    key: 'title',
                    label: this.$t('common.title'),
                    sortable: true,
                },
                {
                    key: 'tag',
                    label: 'Tag',
                    sortable: false,
                },
                {
                    key: 'teachers',
                    label: this.$t('modules.teachers'),
                    sortable: false,
                },
                {
                    key: 'dates',
                    label: this.$t('modules.schedule'),
                    sortable: false,
                },
                {
                    key: 'students',
                    label: this.$t('modules.students'),
                    sortable: false,
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
            downloadUrl: this.routes.baseUrl() + this.routes.modulesDownload(),
            exportUrl: this.routes.baseUrl() + this.routes.modulesExport(),
            moduleTagFilterStorage: `admin-modulelist-filter-${this.$authUser.id}`,
            currentPage: 1,
        };
    },

    created() {
        if (localStorage.getItem(this.moduleTagFilterStorage)) {
            const filterStatuses = JSON.parse(localStorage.getItem(this.moduleTagFilterStorage));
            const now = moment();

            if (now.diff(filterStatuses.expiry, 'hours') > 2) {
                const tagFilterStatus = {
                    tag_filter: this.tagFilter,
                    module_list_current_page: this.currentPage,
                    expiry: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
                };
                localStorage.setItem(this.moduleTagFilterStorage, JSON.stringify(tagFilterStatus));
            } else {
                const tagFilterStatus = JSON.parse(localStorage.getItem(this.moduleTagFilterStorage));
                this.tagFilter = tagFilterStatus.tag_filter;
                this.currentPage = tagFilterStatus.module_list_current_page;
            }
        } else {
            const tagFilterStatus = {
                tag_filter: this.tagFilter,
                module_list_current_page: this.currentPage,
                expiry: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
            };
            localStorage.setItem(this.moduleTagFilterStorage, JSON.stringify(tagFilterStatus));
        }
    },

    mounted() {
        moduleOrds.add(this.modulesIn);
        moduleTagsOrds.add(this.tagsIn);

        moduleOrds
            .with('file', (fileCallback) => {
                fileCallback.with('image');
            })
            .with('module_tags')
            .with('education_levels')
            .with('module_dates', function (q) {
                q.with('module_users'); // todo pull out user model from here relation not set yet
                q.with('module_period', (modulePeriodsCallback) => {
                    modulePeriodsCallback.with('period');
                });
            })
            .with('module_skills', function (moduleSkillCallback) {
                moduleSkillCallback.with('skill');
            })
            .with('module_skill_trees', function (moduleSkillTreesCallback) {
                moduleSkillTreesCallback.with('skillTree');
            })
            .with('module_school_education_levels', function (moduleSchoolEducationLevelCallback) {
                moduleSchoolEducationLevelCallback.with('school_education_level');
            })
            .with('module_teachers', function (moduleTeacherCallback) {
                moduleTeacherCallback.with('user');
            })
            .get()
            .subscribe((modules) => {
                this.allModules = modules;
            });

        moduleTagsOrds.get().subscribe((tags) => {
            this.tags = tags;
        });

        if (localStorage.getItem('module-tag-id') && this.tags.length >= 1) {
            const pos = this.tags.map((tag) => tag.id).indexOf(parseInt(localStorage.getItem('module-tag-id')));
            this.tagFilter =
                pos !== -1
                    ? this.tags[pos]
                    : {
                          id: null,
                          name: 'Alle tags',
                      };
        }
    },

    methods: {
        contextChanged(ctx) {
            const tagFilterStatus = {
                tag_filter: this.tagFilter,
                module_list_current_page: ctx.currentPage,
                expiry: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
            };

            this.currentPage = ctx.currentPage;

            localStorage.setItem(this.moduleTagFilterStorage, JSON.stringify(tagFilterStatus));
        },

        moduleDownload(id) {
            return (window.location.href = this.routes.baseUrl() + this.routes.moduleDownload(id));
        },

        redirectModule(module) {
            window.location.href = this.routes.showModulesTabs(module.id);
        },

        getTeachers(module) {
            var teachers = '';
            for (const teacher of module.item.module_teachers) {
                if (teachers.length === 0) teachers = teacher.user.full_name;
                else teachers += ', ' + teacher.user.full_name;
            }

            return teachers;
        },

        removeModule(module) {
            let students = 0;

            for (const date of module.module_dates) {
                students = students + date.module_users.length;
            }

            if (students > 0) {
                this.alert
                    .confirm(
                        this.$t('modules.delete_module'),
                        this.$t(
                            'modules.be_careful_students_are_registered_here_are_you_sure_you_want_to_delete_this_module',
                        ),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }

                        this.alert.saving();
                        this.axios.delete(this.routes.deleteModule(module.id)).then(
                            (response) => {
                                module.remove();
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    });
            } else {
                this.alert
                    .confirm(
                        this.$t('modules.delete_module'),
                        this.$t('modules.are_you_sure_you_want_to_delete_this_module'),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }
                        this.alert.saving();
                        this.axios.delete(this.routes.deleteModule(module.id)).then(
                            (response) => {
                                module.remove();
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    });
            }
        },

        update(updated) {
            this.tagFilter = updated;
            const tagFilterStatus = {
                tag_filter: this.tagFilter,
                expiry: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
                module_list_current_page: this.$refs.modulesListTable.current_page,
            };

            localStorage.setItem(this.moduleTagFilterStorage, JSON.stringify(tagFilterStatus));
        },
        openCreateModal(module = null) {
            this.$refs['create-module-modal'].show(module);
        },
    },
    computed: {
        modules() {
            let modules = this.allModules;

            if (this.tagFilter && this.tagFilter.id !== null) {
                modules = modules.filter((module) => module.module_tags[0].id === this.tagFilter.id);
            }

            return modules;
        },

        availableTags() {
            if (this.allModules.length >= 1) {
                const tagsId = [];
                let tagsArray = [];

                for (const module of this.allModules) {
                    for (const tag of module.module_tags) {
                        if (tagsId.length === 0) {
                            tagsId.push(tag.id);
                        } else {
                            tagsId.indexOf(tag.id) === -1 && tagsId.push(tag.id);
                        }
                    }
                }

                tagsArray = this.tags.filter((tag) => {
                    return tagsId.indexOf(tag.id) !== -1 && tag;
                });

                tagsArray.unshift({
                    id: null,
                    name: 'Alle tags',
                });

                return tagsArray;
            }

            return [];
        },
    },
};
</script>

<style scoped lang="scss">
.module-management-table ::v-deep {
    .apprentice-table-header {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }

    .tools {
        display: flex;
        flex-direction: column;

        .module-tag-select {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        .btn {
            width: 100%;

            &:not(:last-child) {
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
                margin-right: map-get($grid-gutter-spacing, 'eight');
            }
        }
    }

    table {
        tbody {
            .actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                column-gap: map-get($grid-gutter-spacing, 'fifteen');
            }
        }
    }
}

.module-students {
    display: inline-block;
    width: 25px;
    text-align: right;
    margin-right: map-get($grid-gutter-spacing, 'eight');
}

.module {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.pagination {
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
    justify-content: center;
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .module-management-table ::v-deep {
        .apprentice-table-header {
            margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
        }

        .tools {
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            flex-wrap: wrap;
            column-gap: $grid-gutter-width;
            row-gap: map-get($grid-gutter-spacing, 'ten');

            .module-tag-select {
                margin-bottom: 0;
                width: calc(100% - 182px);
            }

            .btn {
                width: auto;

                &:not(:last-child) {
                    margin-bottom: 0;
                    margin-right: 0;
                }
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .module-management-table ::v-deep {
        .tools {
            column-gap: map-get($grid-gutter-spacing, 'fifteen');

            .btn {
                &:not(:last-child) {
                    margin-bottom: 0;
                }
            }

            .module-tag-select {
                width: 324px;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .module-management-table ::v-deep {
        .apprentice-table-header {
            column-gap: $grid-gutter-width;
            flex-wrap: wrap;
        }

        .apprentice-table-filters {
            flex: 0 0 auto;
            margin-bottom: map-get($grid-padding-spacing, 'eight');
        }

        .apprentice-data-table-actions {
            margin-bottom: map-get($grid-padding-spacing, 'eight');
            margin-left: auto;
        }

        .tools {
            justify-content: flex-end;
            flex: 1 0 auto;

            .module-tag-select {
                width: 290px;
            }
        }
    }
}
</style>
